'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { $unleashFlags } from '@nx-frontend/shared/store/unleash-flags';
import { AddonImagePosition, BillingAddonCode, UnleashFeatures } from '@nx-frontend/shared/types';
import { getPaywallActionDiscounts } from './action-discounts';
import { AddonContext } from './addon-context';
import { AddonForm } from './addon-form';
import { AddonSkeleton } from './addon-skeleton';
import { PaywallErrorDialog } from './paywall-error-dialog';
import { PaywallToast } from './paywall-toast';
import {
  $paywallStore,
  applyCouponEvent,
  changeAddonCountEvent,
  fetchOrderSummaryEvent,
  fetchPaywallEvent,
  initPaywallSettingsEvent,
} from './store';
import { useGetAddonDescription, getAddonImage, useGetAddonTitle } from './util';

interface AddonProps {
  code: BillingAddonCode;
  settings: {
    gatewayUrl: string;
    userBillingUrl: string;
    sessionId: string;
    orgId: string;
    userId: number;
  };
  onClose: () => void;
}

export function Addon(props: AddonProps) {
  const [paywallStore, unleashFlags] = useUnit([$paywallStore, $unleashFlags]);
  const [fetchPaywall, initPaywallSettings, changeAddonCount, fetchOrderSummary, applyCoupon] = useUnit([
    fetchPaywallEvent,
    initPaywallSettingsEvent,
    changeAddonCountEvent,
    fetchOrderSummaryEvent,
    applyCouponEvent,
  ]);
  const { code, onClose, settings } = props;
  const [isAddonLoaded, setIsAddonLoaded] = useState(false);

  const action = useMemo(() => {
    return unleashFlags[UnleashFeatures.SHOW_DISCOUNT_ACTION] ? getPaywallActionDiscounts() : null;
  }, [unleashFlags]);

  const getAddonTitle = useGetAddonTitle();
  const getAddonDescription = useGetAddonDescription();

  const title = getAddonTitle(code);
  const description = getAddonDescription(code, {
    date: paywallStore.currentPeriodEndsAt
      ? dayjs(new Date(paywallStore.currentPeriodEndsAt)).format('MMM D, YYYY')
      : undefined,
  });
  const {
    image,
    imagePosition = AddonImagePosition.CENTER,
    imageBgClassNames,
    imageAddClassNames,
    imageAddStyles,
    imageBgAddStyles,
  } = useMemo(() => getAddonImage(code), [code]);

  const rightClassNames = classNames(
    {
      'items-end justify-end': imagePosition === AddonImagePosition.BOTTOM_RIGHT,
      'items-center justify-end': imagePosition === AddonImagePosition.LEFT,
      'items-center justify-center': imagePosition === AddonImagePosition.CENTER,
    },
    imageBgClassNames && imageBgClassNames
  );

  const { toast } = useToast();

  const showSuccessToast = useCallback(
    (description: string) => {
      toast({
        description: <PaywallToast description={description} />,
      });
    },
    [toast]
  );

  useEffect(() => {
    initPaywallSettings({ settings: { ...settings, showSuccessToast, closePaywall: onClose, addonMode: true } });
    fetchPaywall((store) => {
      changeAddonCount({ code, quantity: code === BillingAddonCode.AI ? store.onlyMembersCnt : 1 });
      fetchOrderSummary(() => {
        setIsAddonLoaded(true);
        const coupon = action?.coupon;
        if (coupon) {
          applyCoupon(coupon);
        }
      });
    });
    // call effect once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AddonContext.Provider
      value={{
        action,
      }}
    >
      <div className="w-[824px] min-h-full flex justify-center items-center relative">
        <button className="absolute right-4 top-4" onClick={onClose}>
          <Icon icon={IconsMap.CLOSE} className="w-5 text-black text-opacity-60" />
        </button>
        <div className="addon__wrapper flex items-stretch w-full">
          <div className="w-[52%] basis-[52%] py-14 px-10 rounded-l-2xl min-h-[490px]">
            {!isAddonLoaded ? (
              <div className="w-full h-full flex items-center justify-center">
                <AddonSkeleton />
              </div>
            ) : (
              <>
                {action?.discount ? (
                  <div className="relative bottom-[20px] text-xs font-medium text-rose-500 inline-block py-0.5 px-2 border border-rose-100 bg-rose-50 rounded-md">
                    Save {action.discount}%
                  </div>
                ) : null}
                <h1 className="addon__title font-bold text-2xl max-w-[330px]">{title}</h1>
                <div className="addon__description text-sm text-neutral-500 mt-2.5">{description}</div>
                <AddonForm code={code} />
              </>
            )}
          </div>
          <div
            className={classNames('w-[48%] basis-[48%] rounded-r-2xl flex overflow-hidden', rightClassNames)}
            style={imageBgAddStyles ?? {}}
          >
            {image ? (
              <img className={classNames(imageAddClassNames)} src={image} alt={title} style={imageAddStyles ?? {}} />
            ) : null}
          </div>
        </div>
      </div>
      {paywallStore.confirmError ? <PaywallErrorDialog /> : null}
    </AddonContext.Provider>
  );
}
